import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { RootStateTypeExtra } from '@store/types'

import IdleTimeHandler from '@components/idle-time-handler'
import { PanoramaType } from '@components/showcase-pannellum/types'

import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import { ProjectType } from '@adUtilities/constants/common'

import GalleryTab from './GalleryTab'

interface PropertiesFooterButtonsProps {
  activeTab: string
  isVideoPlaying: boolean
  isConnected: boolean
  shouldShowGalleryTab: boolean
  hasGeneralExternalLink: boolean
  onTabChange: (arg: string) => void
  onOpenGeneralExternalLinkFrame: () => void
}

function PropertiesFooterButtons({
  activeTab,
  isConnected,
  isVideoPlaying,
  shouldShowGalleryTab,
  hasGeneralExternalLink,
  onTabChange,
  onOpenGeneralExternalLinkFrame,
}: PropertiesFooterButtonsProps) {
  const {
    projectType,
    activeBlock,
    activeLevel,
    activePrecinctId,
    activeStageId,
    blockKeys,
    panoramic,
    precinctList,
    panoramicButtonTitle,
  } = useSelector((state: RootStateTypeExtra) => ({
    projectType: state.projectConfig.type,
    panoramic: state.panoramic,
    activeBlock: state.building.activeBlock,
    activeLevel: state.building.activeLevel,
    activePrecinctId: state.houseAndLand.activePrecinctId,
    activeStageId: state.houseAndLand.activeStageId,
    blockKeys: Object.keys(state.interactivePlan.blocks),
    precinctList: state.houseAndLand.precinctList,
    panoramicButtonTitle: mapWhiteLabelsWithDefaults(state.whiteLabel)
      .floorStageView.panoramicButtonTitle,
  }))

  const hasPanoramicData = useMemo(() => {
    let key: string
    let type: PanoramaType

    if (projectType === ProjectType.HouseAndLand) {
      key =
        precinctList.length > 1
          ? `${activePrecinctId}-${activeStageId}`
          : activeStageId
      type = PanoramaType.Stage
    } else {
      key = blockKeys.length > 1 ? `${activeBlock}-${activeLevel}` : activeLevel
      type = PanoramaType.Level
    }

    return panoramic.some(
      (item) =>
        item.type === type && (item.panoramaGroup || '').toString() === key
    )
  }, [panoramic, activeBlock, activeLevel, activePrecinctId, activeStageId])

  const showFooter =
    (shouldShowGalleryTab || hasPanoramicData || hasGeneralExternalLink) &&
    !isVideoPlaying &&
    !isConnected

  if (!showFooter) {
    return null
  }

  return (
    <IdleTimeHandler>
      <div className="absolute bottom-5 right-5 z-3">
        <div className="flex items-center">
          {shouldShowGalleryTab && (
            <GalleryTab activeTab={activeTab} onTabChange={onTabChange} />
          )}

          {hasPanoramicData && (
            <Link
              to="panoramic"
              className="text-md ml-2 rounded-md border border-mainColour bg-white px-8 py-2 font-medium tracking-widest text-mainColour"
            >
              {panoramicButtonTitle}
            </Link>
          )}

          {hasGeneralExternalLink && (
            <button
              type="button"
              onClick={onOpenGeneralExternalLinkFrame}
              className="text-md ml-2 rounded-md border border-mainColour bg-white px-8 py-2 font-medium tracking-widest text-mainColour"
            >
              Load External Link
            </button>
          )}
        </div>
      </div>
    </IdleTimeHandler>
  )
}

export default PropertiesFooterButtons
